import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';

import { ToastService } from '../../services/toast-service';
import { ConfigService } from '../../services/config-service';
import { ArcgisDomainService } from '../../services/arcgis-domain-service';

const SLOW_REQUEST_WARN_MS = 5000;
const TOAST_COOLDOWN_MS = 8000;

const shownErrors = new Map<string, number>();

function addCacheHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
  if (req.method !== 'GET') return req;
  return req.clone({
    setHeaders: {
      'Cache-Control': 'no-cache',
    },
  });
}

function getErrorMessage(status: number, error: HttpErrorResponse): string {
  switch (status) {
    case 400: return error.error?.message ?? 'Solicitud inválida';
    case 401: return 'No autorizado';
    case 403: return 'Acceso denegado';
    case 404: return 'Recurso no encontrado';
    case 408: return 'La solicitud expiró';
    case 422: return error.error?.message ?? 'Error de validación';
    case 429: return 'Demasiadas solicitudes. Intente de nuevo más tarde';
    case 500: return 'Error interno del servidor';
    case 502: return 'Servidor temporalmente no disponible';
    case 503: return 'Servicio no disponible';
    case 504: return 'Tiempo de espera del servidor agotado';
    default: return 'Ha ocurrido un error inesperado';
  }
}

function isNetworkError(error: HttpErrorResponse): boolean {
  return error.status === 0 && error.error instanceof ProgressEvent;
}

function shouldSuppressToast(url: string, status: number, groupKey?: string): boolean {
  const key = groupKey ?? `${status}:${url}`;
  const lastShown = shownErrors.get(key);
  const now = Date.now();
  if (lastShown && now - lastShown < TOAST_COOLDOWN_MS) {
    return true;
  }
  shownErrors.set(key, now);
  if (shownErrors.size > 50) {
    const oldest = shownErrors.keys().next().value;
    if (oldest) shownErrors.delete(oldest);
  }
  return false;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const arcgisDomain = inject(ArcgisDomainService);
  if (arcgisDomain.isArcgisRequest(req.url)) {
    return next(req);
  }
  const toast = inject(ToastService);
  const config = inject(ConfigService);
  const start = Date.now();

  const reqWithHeaders = addCacheHeaders(req);

  return next(reqWithHeaders).pipe(
    tap({
      next: () => {
        const duration = Date.now() - start;
        if (duration > SLOW_REQUEST_WARN_MS && !config.isProduction) {
          console.warn(`[HTTP] ⏱ ${req.method} ${req.url} — ${duration}ms (slow)`);
        }
      },
    }),
    catchError((error: HttpErrorResponse) => {
      const duration = Date.now() - start;

      if (arcgisDomain.isExternalCorsError(req.url, error.status)) {
        if (!config.isProduction) {
          console.warn(`[HTTP] ↦ CORS bloqueado: ${req.method} ${req.url}`);
        }
        if (shouldSuppressToast(req.url, error.status, 'cors:external')) {
          return throwError(() => error);
        }
        toast.warn('Servicio externo no disponible', 'Algunos mapas externos no pueden cargarse', 6000);
        return throwError(() => error);
      }

      if (isNetworkError(error)) {
        if (shouldSuppressToast(req.url, error.status)) {
          return throwError(() => error);
        }
        if (!navigator.onLine) {
          toast.warn('Sin conexión', 'Verifique su conexión a internet');
        } else {
          toast.error('Error de red', 'No se pudo conectar con el servidor');
        }
        if (!config.isProduction) {
          console.error(`[HTTP] ✗ ${req.method} ${req.url} — ${duration}ms`, error);
        }
        return throwError(() => error);
      }

      if (error.status >= 500) {
        if (shouldSuppressToast(req.url, error.status)) {
          return throwError(() => error);
        }
        toast.error('Error del servidor', 'Ocurrió un error en el servidor. Intente nuevamente.', 8000);
      } else if (error.status >= 400) {
        if (shouldSuppressToast(req.url, error.status)) {
          return throwError(() => error);
        }
        const title = `Error HTTP ${error.status}`;
        const message = getErrorMessage(error.status, error);
        toast.warn(title, message);
      }

      if (!config.isProduction) {
        console.error(`[HTTP] ✗ ${req.method} ${req.url} — ${duration}ms`, error);
      }

      return throwError(() => error);
    })
  );
};
